/**
 * @file TransferSkuField.tsx
 * @description Первое поле панели перемещения: скан артикула или «Подбор» из
 * остатков склада-источника (phase19.24 этап 1, шаг 1).
 *
 * Общий `SkuSelectField` здесь не подходит: он ищет по всему каталогу
 * (`GET /admin/wms/skus`), а увезти можно только то, что лежит на складе-
 * источнике. Выбор фасовки без остатка завёл бы кладовщика в тупик — список
 * партий пустой, добавить нечего.
 *
 * Поэтому разрешение идёт по локальному списку `skus`: точное совпадение по
 * артикулу, иначе единственное совпадение по названию. Не нашлось — подсказка
 * и «Подбор» со списком остатков.
 *
 * Раскладка и подпись — те же, что в приёмке и списании (`SkuSelectField`):
 * поле и «Подбор» в одной строке. Распознавание идёт и по Enter, и по уходу
 * из поля. Фокус в «Партию» переводит форма эффектом по выбранной фасовке —
 * колбэк отрабатывал до перерисовки, когда триггер партии ещё `disabled`.
 *
 * После «Подбора» фокус ведёт сам диалог через `nextFieldRef`, и диалог
 * рендерится в обоих состояниях контрола: размонтируй мы его вместе с полем
 * скана — base-ui закрылся бы без возврата фокуса, и курсор падал бы в `body`
 * (дефект повторного QA 2026-09-20).
 */

import { ListPlus } from "lucide-react";
import { useRef, useState, type FocusEvent, type RefObject } from "react";

import { TransferSkuPickerDialog } from "./TransferSkuPickerDialog";
import type { TransferStockSku } from "./useTransferStockOptions";
import { Button } from "../../ui/button";
import { ScanField, type ScanFieldHandle } from "../../ui/ScanField";
import { TRANSFER_NO_LOTS_HINT } from "../document-texts";
import { SkuSelectedCard } from "../SkuSelectedCard";

interface Props {
  /** Остатки склада-источника — единственный источник выбора. */
  skus: TransferStockSku[];
  value: TransferStockSku | null;
  onChange: (sku: TransferStockSku | null) => void;
  /** Фасовки, все партии которых уже стоят в документе, — в «Подборе» серые. */
  alreadyAddedIds?: string[];
  /** Переход к уже добавленной строке документа (прокрутка + подсветка). */
  onGoToExisting?: (skuId: string) => void;
  /** Поле панели после фасовки («Партия») — туда уходит курсор. */
  nextFieldRef?: RefObject<HTMLElement | null>;
  disabled?: boolean;
}

export function TransferSkuField({
  skus,
  value,
  onChange,
  alreadyAddedIds = [],
  onGoToExisting,
  nextFieldRef,
  disabled = false,
}: Props) {
  const scanRef = useRef<ScanFieldHandle>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  function select(sku: TransferStockSku) {
    onChange(sku);
    setHint(null);
  }

  /** Уход из поля: набранный руками артикул не должен пропасть. */
  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    // Фокус ушёл на «Подбор» в той же строке — это не «уход из поля».
    if (event.currentTarget.contains(event.relatedTarget)) return;
    const code = (event.target as HTMLInputElement).value?.trim();
    if (!code || disabled) return;
    resolve(code);
  }

  function resolve(code: string) {
    const needle = code.trim().toLowerCase();
    const exact = skus.find((sku) => sku.code.toLowerCase() === needle);
    const matches = skus.filter(
      (sku) =>
        sku.code.toLowerCase().includes(needle) ||
        sku.name.toLowerCase().includes(needle),
    );
    const picked = exact ?? (matches.length === 1 ? matches[0] : undefined);
    if (picked) {
      select(picked);
      return;
    }
    setHint(
      matches.length === 0
        ? `На складе нет остатка по «${code}»`
        : `Найдено ${matches.length} совпадений — выберите через «Подбор»`,
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {value ? (
        <SkuSelectedCard
          title={value.name}
          subtitle={`${value.code} · ${value.totalQuantity} шт`}
          disabled={disabled}
          onReset={() => onChange(null)}
        />
      ) : (
        <>
          <div className="flex min-w-0 gap-2" onBlur={handleBlur}>
            <div className="min-w-0 flex-1">
              <ScanField
                ref={scanRef}
                disabled={disabled || skus.length === 0}
                helperText={
                  skus.length === 0
                    ? TRANSFER_NO_LOTS_HINT
                    : "Скан или точный артикул выбирает фасовку, другой текст — поиск"
                }
                onSubmit={(code) => resolve(code)}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              // preventDefault: кнопка стоит внутри `<label>` поля, и без него
              // браузер трактует клик как клик по подписи и уводит фокус в input.
              onClick={(e) => {
                e.preventDefault();
                setPickerOpen(true);
              }}
              disabled={disabled || skus.length === 0}
              className="shrink-0 self-start"
              title="Открыть остатки склада"
            >
              <ListPlus className="size-4" />
              <span className="ml-1.5">Подбор</span>
            </Button>
          </div>
          {hint ? <p className="text-xs text-warning">{hint}</p> : null}
        </>
      )}

      <TransferSkuPickerDialog
        nextFieldRef={nextFieldRef}
        open={pickerOpen}
        skus={skus}
        alreadyAddedIds={alreadyAddedIds}
        onGoToExisting={(skuId) => {
          setPickerOpen(false);
          onGoToExisting?.(skuId);
        }}
        onSelect={(sku) => {
          setPickerOpen(false);
          select(sku);
        }}
        onClose={() => {
          setPickerOpen(false);
          scanRef.current?.focus();
        }}
      />
    </div>
  );
}
